import { formatDateTime } from "@/lib/time";
import type * as fhirTypes from "fhir/r4";

/**
 * Given a single DocumentReference, returns the description with timestamp placeholders replaced.
 * Handles replacing {timestamp} with the actual formatted timestamp from the document.
 * Optionally appends a visit type label (e.g. "ASYNC DX") to the summary tag.
 */
export function updateSubjectiveSummaryTag(
  documentReference: fhirTypes.DocumentReference,
  visitTypeLabel?: string,
): string {
  const description = documentReference.description;
  if (!description) return "";

  const authorDisplay = documentReference.author?.[0]?.display ?? "";
  const date = documentReference.date;

  const subjectiveTimestamp = formatDateTime(date);
  const visitTypeSuffix = visitTypeLabel ? ` - ${visitTypeLabel}` : "";
  const updatedSummaryTag = `**[${authorDisplay} summary ${subjectiveTimestamp}${visitTypeSuffix}]** `;

  // Match both the initial `{timestamp}` placeholder and already-expanded tags
  // (e.g. after a save, the stored description contains the real timestamp, not
  // the placeholder, so a literal startsWith check would fail).
  const escapedAuthor = authorDisplay.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const summaryTagPattern = new RegExp(
    `^\\*\\*\\[${escapedAuthor} summary [^\\]]+\\]\\*\\* `,
  );

  return summaryTagPattern.test(description)
    ? description.replace(summaryTagPattern, updatedSummaryTag)
    : description;
}
